import {EVM_NETWORKS, EVM_TOKENS} from '#/config/evm'
import {fetchCoingeckoJson, isRateLimited} from './coingecko'
import {DEFAULT_WATCHLIST_IDS, resolveCoingeckoMeta} from './marketData'

export type MarketSearchResult = {
  id: string
  symbol: string
  name: string
  imageUrl?: string
  marketCapRank?: number | null
}

const SEARCH_TTL = 120_000
const searchCache = new Map<string, {ts: number; data: MarketSearchResult[]}>()

const FALLBACK_PRIORITY = new Map(
  DEFAULT_WATCHLIST_IDS.map((id, index) => [id, index + 1]),
)

const FALLBACK_INDEX: MarketSearchResult[] = (() => {
  const map = new Map<string, MarketSearchResult>()
  const add = (id: string, symbol?: string, name?: string) => {
    if (!id || map.has(id)) return
    const meta = resolveCoingeckoMeta(id)
    map.set(id, {
      id,
      symbol: symbol || meta.symbol,
      name: name || meta.name,
      marketCapRank: FALLBACK_PRIORITY.get(id) ?? null,
    })
  }

  DEFAULT_WATCHLIST_IDS.forEach(id => add(id))
  Object.values(EVM_NETWORKS).forEach(network => {
    add(network.nativeCoingeckoId, network.nativeSymbol, network.nativeName)
  })
  Object.values(EVM_TOKENS).forEach(tokens => {
    tokens.forEach(token => add(token.coingeckoId, token.symbol, token.name))
  })

  return Array.from(map.values())
})()

const searchFallback = (query: string) => {
  const q = query.toLowerCase()
  return FALLBACK_INDEX.filter(
    item =>
      item.id.toLowerCase().includes(q) ||
      item.symbol.toLowerCase().includes(q) ||
      item.name.toLowerCase().includes(q),
  ).sort((a, b) => {
    if (a.marketCapRank == null && b.marketCapRank == null) return 0
    if (a.marketCapRank == null) return 1
    if (b.marketCapRank == null) return -1
    return a.marketCapRank - b.marketCapRank
  })
}

export async function searchMarket(query: string) {
  const normalized = query.trim().toLowerCase()
  if (!normalized) return []

  const cached = searchCache.get(normalized)
  if (cached && Date.now() - cached.ts < SEARCH_TTL) {
    return cached.data
  }

  const fallbackResults = searchFallback(normalized)
  if (normalized.length < 3) {
    if (fallbackResults.length) {
      searchCache.set(normalized, {ts: Date.now(), data: fallbackResults})
    }
    return fallbackResults
  }

  try {
    const data = await fetchCoingeckoJson<{
      coins?: Array<{
        id: string
        name: string
        symbol: string
        thumb?: string
        large?: string
        market_cap_rank?: number | null
      }>
    }>('search', {query: normalized})

    const results = (data.coins || [])
      .map(item => ({
        id: item.id,
        symbol: item.symbol?.toUpperCase() || item.id.toUpperCase(),
        name: item.name || item.id,
        imageUrl: item.large || item.thumb,
        marketCapRank: item.market_cap_rank ?? null,
      }))
      .sort((a, b) => {
        if (a.marketCapRank == null && b.marketCapRank == null) return 0
        if (a.marketCapRank == null) return 1
        if (b.marketCapRank == null) return -1
        return a.marketCapRank - b.marketCapRank
      })

    if (fallbackResults.length) {
      const merged = new Map<string, MarketSearchResult>()
      results.forEach(item => merged.set(item.id, item))
      fallbackResults.forEach(item => {
        if (!merged.has(item.id)) merged.set(item.id, item)
      })
      const mergedResults = Array.from(merged.values())
      searchCache.set(normalized, {ts: Date.now(), data: mergedResults})
      return mergedResults
    }

    searchCache.set(normalized, {ts: Date.now(), data: results})
    return results
  } catch (err) {
    if (cached) return cached.data
    if (isRateLimited(err)) {
      if (fallbackResults.length) {
        return fallbackResults
      }
      throw err
    }
    throw err
  }
}
